import React from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { EquipmentState, GetDetailedEquipmentQuery, useUpdateEquipmentStateMutation } from 'data/__generated__';

import { StateHistory } from 'components/DetailsView';
import { ColumnsSectionContainer, SectionColumn } from 'components/DetailsView/Section';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { StatusTag } from 'pages/Equipments/components/StatusTag';
import { confirmPopup } from 'components/Popups/Confirm';

export const StatesSection = ({
    equipment,
    isDirty,
}: {
    equipment: NonNullable<GetDetailedEquipmentQuery['detailedEquipment']>;
    isDirty: boolean;
}) => {
    const { _id: equipmentId, stateHistory, state } = equipment;

    const [updateEquipmentState] = useUpdateEquipmentStateMutation();

    const formattedStateHistory = stateHistory.map((stateRecord) => ({
        ...stateRecord,
        state: <StatusTag state={stateRecord.state} />,
    }));

    const STATE_CHANGE_ACTIONS = [
        {
            label: 'Passer équipment en statut régulier',
            stateToSet: EquipmentState.Regular,
            isDisabled: state === EquipmentState.Regular,
        },
        {
            label: "Archiver l'équipment",
            stateToSet: EquipmentState.Archived,
            isDisabled: state === EquipmentState.Archived,
        },
    ];

    const handleStateUpdate = async (stateToSet: EquipmentState) => {
        const { data } = await updateEquipmentState({
            variables: {
                stateToSet,
                equipmentId,
            },
        });

        if (!data) {
            throw Error("Une erreur inconnue s'est produite");
        }

        const {
            updateEquipmentState: { success, error, equipment: updatedEquipment },
        } = data;

        if (success && updatedEquipment) {
            toast.success('Le statut du produit a bien été modifié !');
            return;
        }

        if (error) {
            toast.error(error, { autoClose: false });
            return;
        }

        throw Error("Une erreur inconnue s'est produite");
    };

    const updateState = async (
        e: React.MouseEvent<HTMLElement, MouseEvent>,
        label: string,
        stateToSet: EquipmentState,
    ) => {
        e.preventDefault();
        const hasConfirmed = await confirmPopup({
            title: 'Confirmer',
            message: `Êtes-vous sûr.e de vouloir ${label.toLowerCase()} ?`,
        });
        if (hasConfirmed) {
            handleStateUpdate(stateToSet);
        }
    };

    return (
        <SeparatorContainer>
            <ColumnsSectionContainer numberOfColumns={3}>
                <StateHistory label="Historique de statuts" records={formattedStateHistory} />
                <StatusTag state={state} showHeading />

                <SectionColumn>
                    {isDirty ? (
                        <WarningText>
                            ⚠️ Veuillez vous assurer d'avoir sauvegardé ou annulé vos ajustements avant de mettre à jour
                            le statut
                        </WarningText>
                    ) : null}
                    <OverlaySectionColumn $isDirty={isDirty}>
                        {STATE_CHANGE_ACTIONS.map((stateChange, index) => {
                            const { stateToSet, label, isDisabled } = stateChange;

                            return (
                                <TotemPrimaryButton
                                    key={index}
                                    type="button"
                                    onClick={(event) => updateState(event, label, stateToSet)}
                                    disabled={isDisabled || isDirty}
                                >
                                    {label}
                                </TotemPrimaryButton>
                            );
                        })}
                    </OverlaySectionColumn>
                </SectionColumn>
            </ColumnsSectionContainer>
        </SeparatorContainer>
    );
};

const SeparatorContainer = styled.div`
    margin-top: 30px;
    padding-top: 15px;
    border-top: ${({ theme }) => `1px solid ${theme.lightBorderColor}`};
`;

const WarningText = styled.div`
    color: ${({ theme }) => theme.textColor};
    font-weight: 800;
`;

const OverlaySectionColumn = styled(SectionColumn)<{ $isDirty: boolean }>`
    opacity: ${({ $isDirty }) => ($isDirty ? 0.4 : 1)};
`;
